import { createContext, useContext, useEffect, useMemo, useState, useCallback } from "react";

const CartContext = createContext(null);
const STORAGE_KEY = "kh_cart_v1";

function loadCart() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

export const CartProvider = ({ children }) => {
  const [items, setItems] = useState(loadCart);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
    } catch {
      /* ignore quota errors */
    }
  }, [items]);

  // line shape: { variantId, handle, title, variantTitle, image, price:{amount,currency},
  //               options:[{name,value}], brand, quantity, maxQty }
  const addItem = useCallback((line, qty = 1) => {
    const cap = (Number.isFinite(line.maxQty) && line.maxQty > 0) ? line.maxQty : 99;
    let clamped = false;
    setItems((prev) => {
      const idx = prev.findIndex((i) => i.variantId === line.variantId);
      if (idx >= 0) {
        const next = [...prev];
        const desired = next[idx].quantity + qty;
        const final = Math.min(cap, desired);
        if (final < desired) clamped = true;
        next[idx] = { ...next[idx], ...line, quantity: Math.max(1, final) };
        return next;
      }
      const final = Math.min(cap, Math.max(1, qty));
      if (final < qty) clamped = true;
      return [...prev, { ...line, quantity: final }];
    });
    setOpen(true);
    return { clamped, cap };
  }, []);

  const updateQty = useCallback((variantId, quantity) => {
    setItems((prev) =>
      prev
        .map((i) => {
          if (i.variantId !== variantId) return i;
          const cap = (Number.isFinite(i.maxQty) && i.maxQty > 0) ? i.maxQty : 99;
          return { ...i, quantity: Math.min(cap, quantity) };
        })
        .filter((i) => i.quantity > 0)
    );
  }, []);

  const removeItem = useCallback((variantId) => {
    setItems((prev) => prev.filter((i) => i.variantId !== variantId));
  }, []);

  const clear = useCallback(() => setItems([]), []);

  const { count, subtotal, currency } = useMemo(() => {
    let c = 0;
    let s = 0;
    let cur = "EUR";
    for (const i of items) {
      c += i.quantity;
      if (i.price?.amount) s += i.price.amount * i.quantity;
      if (i.price?.currency) cur = i.price.currency;
    }
    return { count: c, subtotal: s, currency: cur };
  }, [items]);

  const value = useMemo(
    () => ({ items, addItem, updateQty, removeItem, clear, count, subtotal, currency, open, setOpen }),
    [items, addItem, updateQty, removeItem, clear, count, subtotal, currency, open]
  );

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
};

export const useCart = () => {
  const ctx = useContext(CartContext);
  if (!ctx) throw new Error("useCart must be used within a CartProvider");
  return ctx;
};
